import { useState, type ReactNode } from "react";
import { printSignFile } from "../api/client";
import { statusClass, statusLabel, when } from "../lib/format";
import type { SignDoc } from "../types";
import { IcoArchive, IcoCopy, IcoDownload, IcoEye, IcoLink, IcoPen, IcoPrint, IcoSend, IcoShield, IcoTrash } from "../ui/icons";

function Tip({
  label,
  onClick,
  children,
  danger = false,
  sure = false,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
  danger?: boolean;
  sure?: boolean;
}) {
  return (
    <button
      type="button"
      className={`sg-tip${danger ? " is-del" : ""}${sure ? " is-sure" : ""}`}
      data-tip={label}
      aria-label={label}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

export function FileRow({
  doc,
  onEdit,
  onMail,
  onMove,
  onClone,
  onArchive,
  onTrace,
  onPieces,
  onDownload,
  onCopy,
  onDelete,
  archived = false,
  mailing = false,
}: {
  doc: SignDoc;
  onEdit: () => void;
  onMail: () => void;
  onMove: () => void;
  onClone: () => void;
  onArchive: () => void;
  onTrace: () => void;
  onPieces: () => void;
  onDownload: () => void;
  onCopy?: () => void;
  onDelete?: () => void;
  archived?: boolean;
  mailing?: boolean;
}) {
  const draft = doc.status === "draft";
  const signed = doc.status === "signed";
  const canCopy = !draft && !signed && !!onCopy;
  const canDelete = draft && !!onDelete;
  const hasPieces = !draft && ((doc.asks?.length ?? 0) > 0 || (doc.pieces?.length ?? 0) > 0);
  const mailLabel = mailing ? "Envoi…" : draft ? "Envoyer" : signed ? "Renvoyer le pack" : "Renvoyer le mail";
  const [sure, setSure] = useState(false);

  return (
    <article className="sg-row" onClick={onEdit}>
      <div className="sg-row__top">
        <div className="sg-row__body">
          <div className="sg-row__icon" aria-hidden="true" />
          <div className="sg-row__meta">
            <strong>{doc.title}</strong>
            <span>{doc.created_by || "Suite"}</span>
            <span>{when(doc.created_at)}</span>
            <em className={statusClass(doc)}>{statusLabel(doc)}</em>
          </div>
        </div>
        <div className="sg-row__mail" onClick={(e) => e.stopPropagation()}>
          <Tip label={mailLabel} onClick={() => !mailing && onMail()}>
            <IcoSend />
          </Tip>
        </div>
      </div>
      <div className="sg-row__acts" onClick={(e) => e.stopPropagation()}>
        {canCopy ? (
          <button type="button" className="sg-linkcopy" onClick={onCopy}>
            <IcoLink />
            Copier le lien
          </button>
        ) : null}
        <Tip label={draft ? "Modifier" : "Voir"} onClick={onEdit}>
          {draft ? <IcoPen /> : <IcoEye />}
        </Tip>
        {hasPieces ? (
          <Tip label="Pièces" onClick={onPieces}>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M15.4 7.2 8.7 14a2.4 2.4 0 0 0 3.4 3.4l7.2-7.2a3.6 3.6 0 0 0-5.1-5.1L7 12.3" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Tip>
        ) : null}
        <Tip label="Déplacer" onClick={onMove}>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M4 8V6.5A1.5 1.5 0 0 1 5.5 5H10l2 2h6.5A1.5 1.5 0 0 1 20 8.5V18a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 18V8Z" fill="none" stroke="currentColor" strokeWidth="1.8" />
            <path d="M9 13h6m0 0-2-2m2 2-2 2" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </Tip>
        <Tip label="Cloner" onClick={onClone}>
          <IcoCopy />
        </Tip>
        {!draft ? (
          <Tip label="Imprimer" onClick={() => printSignFile(doc)}>
            <IcoPrint />
          </Tip>
        ) : null}
        {!draft ? (
          <Tip label="Télécharger" onClick={onDownload}>
            <IcoDownload />
          </Tip>
        ) : null}
        {!draft ? (
          <Tip label="Intégrité" onClick={onTrace}>
            <IcoShield />
          </Tip>
        ) : null}
        {canDelete ? (
          <Tip
            label={sure ? "Confirmer la suppression" : "Supprimer"}
            danger
            sure={sure}
            onClick={() => {
              if (!sure) {
                setSure(true);
                window.setTimeout(() => setSure(false), 2500);
                return;
              }
              setSure(false);
              onDelete?.();
            }}
          >
            <IcoTrash />
          </Tip>
        ) : null}
        {archived || !signed ? (
          <Tip label={archived ? "Restaurer" : "Archiver"} onClick={onArchive}>
            <IcoArchive />
          </Tip>
        ) : null}
      </div>
    </article>
  );
}
